// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AdvisoryDebtAccount } from '@shared/data-access/types/brokerage-types';
import { groupBy } from '@shared/ui-kit';
import {
  getLoanMaturityDate,
  getPendingDebtAmortizations,
  resolveInstitutionLabel,
} from './debt-amortizations';

export type LoansTableRow = {
  id: string;
  bankName: string;
  name: string;
  grantedCapital: number | null | undefined;
  pendingAmount: number | null | undefined;
  interestRate: number | null | undefined;
  maturityDate: string | null;
  monthlyPayment: number | null | undefined;
};

export type GroupedLoansTableRow = LoansTableRow & {
  subRows: LoansTableRow[];
};

export const buildLoansTableRows = (
  accounts: AdvisoryDebtAccount[] = [],
  institutionNames: Map<string, string> = new Map(),
): LoansTableRow[] =>
  accounts.flatMap(account =>
    (account.loans ?? []).map(loan => {
      const pendingAmortizations = getPendingDebtAmortizations(loan.amortizations);
      const maturityDate = getLoanMaturityDate(loan);

      return {
        id: loan.provider_uuid,
        bankName: resolveInstitutionLabel(account.institution_id, institutionNames),
        name: loan.name ?? '-',
        grantedCapital: loan.granted_capital,
        pendingAmount: loan.pending_amount,
        interestRate: loan.interest_rate,
        maturityDate,
        monthlyPayment: loan.next_installment ?? pendingAmortizations[0]?.total,
      };
    }),
  );

export const buildGroupedLoansTableRows = (
  rows: LoansTableRow[] = [],
): GroupedLoansTableRow[] => {
  if (rows.length === 0) {
    return [];
  }

  const banks = groupBy(rows, 'bankName');

  return Array.from(banks.entries())
    .sort(([bankA], [bankB]) => bankA.localeCompare(bankB))
    .map(([bankName, subRows]) => {
      const grantedCapital = subRows.reduce(
        (acc, row) => acc + (row.grantedCapital ?? 0),
        0,
      );

      const pendingAmount = subRows.reduce(
        (acc, row) => acc + (row.pendingAmount ?? 0),
        0,
      );

      const monthlyPayment = subRows.reduce(
        (acc, row) => acc + (row.monthlyPayment ?? 0),
        0,
      );

      return {
        id: `bank-${bankName}`,
        bankName,
        name: '',
        grantedCapital,
        pendingAmount,
        interestRate: null,
        maturityDate: null,
        monthlyPayment,
        subRows,
      };
    });
};
